import { forwardRef, useMemo } from 'react';

import Link from 'next/link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useTheme } from 'hooks/useTheme';

import Loader from 'components/atom/Loader';

import { ButtonContainer, AContainer, LinkContainer } from './styles';
import { getTheme } from './themes';
import { IButtonDefaultProps, IAProps } from './types';

const Button = forwardRef<any, IButtonDefaultProps>(
  (
    {
      theme = 'default',
      type = 'button',
      text,
      icon,
      iconPosition = 'left',
      fontSize = undefined,
      isLoading = false,
      isATag = false,
      isLinkTag = false,
      isSmall = false,
      isDisabled = false,
      disabledUppercase = false,
      className,
      target,
      rel,
      href,
      download,
      ...rest
    },
    ref,
  ) => {
    const { theme: globalTheme } = useTheme();

    const themeProps = useMemo(() => getTheme(theme, globalTheme), [globalTheme, theme]);

    const Content = useMemo(
      (): React.ReactElement =>
        isLoading ? (
          <Loader color={themeProps.front} disabeldPadding />
        ) : (
          <>
            {iconPosition === 'left' && icon && <FontAwesomeIcon icon={icon} />}
            {text}
            {iconPosition === 'right' && icon && <FontAwesomeIcon icon={icon} />}
          </>
        ),
      [icon, iconPosition, isLoading, text, themeProps.front],
    );

    const props = {
      fontSize,
      isIconOnly: text === undefined,
      iconPosition,
      buttonTheme: themeProps,
      className: `button button-${theme} ${className || ''}`,
      isSmall,
      disabledUppercase,
    };

    const aProps: IAProps = {};
    if (href) aProps.href = href;
    if (target) aProps.target = target;
    if (rel) aProps.rel = rel;
    if (download) aProps.download = download;

    return isATag ? (
      <AContainer ref={ref} {...aProps} {...props}>
        {Content}
      </AContainer>
    ) : (
      <>
        {isLinkTag ? (
          <LinkContainer {...aProps} {...props}>
            <Link ref={ref} href={aProps.href || '#!'}>
              {Content}
            </Link>
          </LinkContainer>
        ) : (
          <ButtonContainer
            ref={ref}
            type={type}
            {...rest}
            {...props}
            disabled={isLoading || isDisabled}
          >
            {Content}
          </ButtonContainer>
        )}
      </>
    );
  },
);

export default Button;
